import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Download, LogOut, Search, PlaneLanding, CheckCircle2, ShieldAlert,
         Briefcase, Building2, Users, PlaneTakeoff, Home, LayoutDashboard, Send,
         DollarSign, FileText, ClipboardCheck, ClipboardPaste, Activity as ActivityIcon, Plug,
         Megaphone, ChevronDown, ChevronRight, Menu, Trash2, Radio, UserX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EmailComposerExtras } from "@/components/EmailComposerExtras";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ADMIN } from "@/constants/testIds";
import { SalesTab } from "@/components/AdminSales";
import { AssessmentsTab } from "@/components/AdminAssessments";
import { ClientsTab } from "@/components/AdminClients";
import { IntegrationsTab } from "@/components/AdminIntegrations";
import { DiscountsTab } from "@/components/AdminDiscounts";
import { EmailHubTab } from "@/components/AdminEmailHub";
import { MembersTab } from "@/components/AdminMembers";
import { OutreachTab } from "@/components/AdminOutreach";
import { WorkspaceTab, PulseTicker } from "@/components/AdminWorkspace";
import { ActivityTab } from "@/components/AdminActivity";
import { ResumeReviewsTab } from "@/components/AdminResumeReviews";
import { FinancePage } from "@/components/AdminFinance";
import { AdManagerTab } from "@/components/AdminAdManager";
import { LiveBoardTab } from "@/components/AdminLiveBoard";
import { SalesFeed } from "@/components/SalesFeed";
import { ShortlistAdmin } from "@/components/shortlist/ShortlistAdmin";
import { CandidateEnquiries } from "@/components/shortlist/CandidateEnquiries";
import { AdminJobIntake } from "@/components/job-matching/AdminJobIntake";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = "ditch_admin_pw";
const ALLOWED_DOMAINS = ["@theditchcollective.co.nz", "@theditchcareers.com.au"];

const ROLE_LABEL = {
  founder: "Founder",
  admin: "Admin",
  recruiter: "Recruiter",
  tdc_manager: "TDC Manager",
  va: "VA",
};

const STATUSES = ["New", "Contacted", "Call Scheduled", "Interviewed", "Converted", "Not a fit", "Settled"];

const PRIORITY_STYLE = {
  Hot: "bg-[#0D2F4D] text-[#C6D21F]",
  Warm: "bg-[#C6D21F] text-[#0D2F4D]",
  Nurture: "bg-[#6F8FA8] text-white",
  Cold: "bg-white text-[#0D2F4D] border border-[#0D2F4D]",
};

function fmt(dt) {
  try {
    const d = new Date(dt);
    return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch { return dt; }
}

function toCSV(leads) {
  const cols = [
    "submitted_at", "first_name", "last_name", "email", "phone_number",
    "current_location", "move_timeline", "lead_priority", "lead_status",
    "who_is_moving", "melbourne_area", "help_needed",
    "employment_situation", "accommodation_budget", "best_call_time", "package_interest", "laura_notes",
  ];
  const esc = (v) => {
    if (v == null) return "";
    const s = Array.isArray(v) ? v.join("; ") : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = cols.join(",");
  const rows = leads.map((l) => cols.map((c) => esc(l[c])).join(","));
  return [head, ...rows].join("\n");
}

function GoogleSignIn({ onLogin, initialError }) {
  const [error, setError] = useState(initialError || null);

  const startSignIn = () => {
    const redirect = `${window.location.origin}/admin`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  if (error === "denied") {
    return (
      <div className="min-h-screen bg-[#0D2F4D] flex items-center justify-center px-5" data-testid="admin-403-page">
        <div className="bg-white rounded-2xl p-8 md:p-10 w-full max-w-md shadow-2xl text-center">
          <div className="w-14 h-14 rounded-full bg-[#FDECEC] text-[#B91C1C] flex items-center justify-center mx-auto mb-5">
            <ShieldAlert size={26} />
          </div>
          <h1 className="font-display text-3xl font-extrabold text-[#0D2F4D] mb-2">403 · Access denied</h1>
          <p className="text-sm text-[#6F8FA8] mb-6">
            Sign-in is restricted to accounts on{" "}
            <b className="text-[#0D2F4D]">{ALLOWED_DOMAINS.join(" or ")}</b>.
            Contact your admin if you believe this is a mistake.
          </p>
          <Button
            data-testid="admin-403-retry"
            onClick={() => { setError(null); startSignIn(); }}
            className="w-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 rounded-full py-6 font-display font-bold"
          >
            Try a different account
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D2F4D] flex items-center justify-center px-5">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardContent className="p-8 md:p-10">
        <div className="flex items-center justify-center mb-6">
          <img src="/ditch-lockup.png" alt="The Ditch Collective" className="h-14 w-auto" />
        </div>
        <p className="text-xs uppercase tracking-wider text-center text-[#6F8FA8] font-semibold mb-2">
          Admin workspace
        </p>
        <h1 className="font-display text-2xl font-extrabold text-center text-[#0D2F4D] mb-6">
          Sign in to continue
        </h1>
        <Button
          type="button"
          data-testid={ADMIN.loginBtn}
          onClick={startSignIn}
          variant="outline"
          className="w-full rounded-full py-6 font-display font-bold border-2 border-[#E4E7EA] hover:border-[#0D2F4D] hover:bg-white text-[#0D2F4D]"
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt=""
            className="w-5 h-5"
          />
          Sign in with Google
        </Button>
        <p className="mt-6 text-xs text-center text-[#6F8FA8] leading-relaxed">
          Only{" "}
          <b className="text-[#0D2F4D]">{ALLOWED_DOMAINS.join(" and ")}</b>{" "}
          accounts have access.
        </p>
        </CardContent>
      </Card>
    </div>
  );
}

function LeadCard({ lead, questionnaire, onStatusChange, onMarkSettled, onDelete, onPromote, selected, onToggleSelect }) {
  const prio = lead.lead_priority || "Warm";
  const settled = Boolean(lead.settled_at) || lead.lead_status === "Settled";
  // Source-chip logic — infer origin from `lead_source` copy the backend
  // stamped on each doc. Kept forgiving: any lead_source containing
  // "Relocator" ⇒ /move, "backfill" ⇒ Legacy, everything else ⇒ Homepage.
  const src = (lead.lead_source || "").toLowerCase();
  const sourceChip = src.includes("backfill")
    ? { label: "Legacy backfill", cls: "bg-[#ECF0F4] text-[#6F8FA8] border-[#D6DCE3]" }
    : (src.includes("relocator") || src.includes("/move"))
      ? { label: "/move", cls: "bg-[#0D2F4D]/10 text-[#0D2F4D] border-[#0D2F4D]/20" }
      : { label: "Homepage", cls: "bg-[#C6D21F]/20 text-[#0D2F4D] border-[#C6D21F]/50" };
  const mergedCount = Number(lead.merged_count || 1);
  return (
    <Card data-testid={ADMIN.leadCard(lead.id)} className={`border-[#E4E7EA] shadow-sm ${selected ? "ring-2 ring-[#C6D21F]" : ""}`}>
      <CardContent className="p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect?.(lead.id)}
            data-testid={`lead-select-${lead.id}`}
            className="w-4 h-4 rounded cursor-pointer mt-1.5 shrink-0"
            title="Select for bulk actions"
          />
          <div>
            <h3 className="font-display text-xl font-bold text-[#0D2F4D] flex items-center gap-2 flex-wrap">
              {lead.first_name} {lead.last_name}
              {settled && (
                <Badge className="bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F] font-bold text-[10px] uppercase tracking-widest">
                  <PlaneLanding size={10} /> Settled
                </Badge>
              )}
              <span
                data-testid={`lead-source-chip-${lead.id}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${sourceChip.cls}`}
                title={lead.lead_source || "Source unknown"}
              >
                {sourceChip.label}
              </span>
              {mergedCount > 1 && (
                <span
                  data-testid={`lead-merged-badge-${lead.id}`}
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FDECEC] text-[#B91C1C] border border-[#B91C1C]/30"
                  title={`This person submitted ${mergedCount} times within 24h — merged into one card.`}
                >
                  Merged ×{mergedCount}
                </span>
              )}
            </h3>
            <p className="text-xs text-[#6F8FA8] mt-1">Submitted {fmt(lead.submitted_at)}</p>
            {settled && lead.settled_at && (
              <p className="text-xs text-[#6F8FA8] mt-0.5">Settled {fmt(lead.settled_at)}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Badge className={`text-xs font-bold uppercase tracking-wide ${PRIORITY_STYLE[prio]}`}>
            {prio}
          </Badge>
          <Select value={lead.lead_status || "New"} onValueChange={(v) => onStatusChange(lead.id, v)}>
            <SelectTrigger
              data-testid={ADMIN.statusSelect(lead.id)}
              className="h-9 w-[140px] rounded-full border-[#D6DCE3] text-sm font-semibold text-[#0D2F4D]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            data-testid={ADMIN.markSettled(lead.id)}
            onClick={() => onMarkSettled(lead)}
            disabled={settled}
            size="sm"
            className={settled
              ? "bg-[#ECF0F4] text-[#6F8FA8] hover:bg-[#ECF0F4] cursor-not-allowed rounded-full"
              : "bg-[#0D2F4D] text-white hover:bg-[#1a4063] rounded-full"}
          >
            {settled ? <><CheckCircle2 size={12} /> Settled</> : <><PlaneLanding size={12} /> Mark settled</>}
          </Button>
          {!lead.promoted_to_candidate_id && (
            <>
              <button
                data-testid={`lead-promote-support-${lead.id}`}
                onClick={() => onPromote(lead, { mode: "job_support" })}
                title="Move to Candidate — Job Support only"
                className="h-9 px-3 rounded-full bg-[#C6D21F] text-[#0D2F4D] text-xs font-bold hover:bg-[#B8C41A]"
              >
                → Job Support
              </button>
              <button
                data-testid={`lead-promote-package-${lead.id}`}
                onClick={() => onPromote(lead, { mode: "package" })}
                title="Move to Candidate — with Package Purchased"
                className="h-9 px-3 rounded-full bg-[#0D2F4D] text-[#C6D21F] text-xs font-bold hover:bg-[#1a4063]"
              >
                → With Package
              </button>
            </>
          )}
          {lead.promoted_to_candidate_id && (
            <span
              data-testid={`lead-promoted-badge-${lead.id}`}
              className="h-9 inline-flex items-center px-3 rounded-full bg-[#ECF0F4] text-[#6F8FA8] text-xs font-bold"
            >
              ✓ Moved to Candidate
            </span>
          )}
          <button
            data-testid={`lead-delete-${lead.id}`}
            onClick={() => onDelete(lead)}
            title="Delete lead"
            className="h-9 w-9 rounded-full border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700 hover:border-red-400 inline-flex items-center justify-center transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-5 text-sm">
        <a href={`tel:${lead.phone_number}`} className="flex items-center gap-2 text-[#0D2F4D] hover:text-[#C6D21F]">
          <Phone size={14} /> {lead.phone_number}
        </a>
        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-[#0D2F4D] hover:text-[#C6D21F]">
          <Mail size={14} /> {lead.email}
        </a>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-5 text-sm">
        <Detail k="Moving from" v={lead.current_location} />
        <Detail k="Timeline" v={lead.move_timeline} />
        <Detail k="Target city" v={lead.target_city || lead.destination_city || lead.melbourne_area} />
        <Detail k="Who's moving" v={lead.who_is_moving || lead.household_type} />
        <Detail k="Situation" v={lead.current_situation || lead.employment_situation} />
        <Detail k="Budget/week" v={lead.budget_level || lead.accommodation_budget} />
        <Detail k="Best time to call" v={lead.best_call_time} />
        <Detail k="Package interest" v={lead.package_interest} />
        <Detail k="Needs help with" v={((lead.support_needed || lead.help_needed) || []).join(", ") || "—"} full />
      </div>

      {lead.laura_notes && (
        <div className="mt-4 p-4 bg-[#F2F2EF] rounded-xl">
          <p className="text-xs font-semibold text-[#6F8FA8] uppercase mb-1">Notes from lead</p>
          <p className="text-sm text-[#0D2F4D] whitespace-pre-line">{lead.laura_notes}</p>
        </div>
      )}

      {questionnaire && (
        <div className="mt-4 p-4 bg-[#ECF0F4] rounded-xl" data-testid={`lead-questionnaire-${lead.id}`}>
          <p className="text-xs font-semibold text-[#6F8FA8] uppercase mb-2">Pre-call questionnaire</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <Detail k="Current city (NZ)" v={questionnaire.current_city} />
            <Detail k="Destination (AUS)" v={questionnaire.destination_city} />
            <Detail k="Job / industry" v={questionnaire.job_type} />
            <Detail k="Family" v={questionnaire.family_situation} />
            <Detail k="Pets" v={questionnaire.pets} />
            <Detail k="Timeline" v={questionnaire.move_timeline} />
            <Detail k="Rent budget (monthly)" v={questionnaire.rent_budget} />
            <Detail k="Biggest concern" v={questionnaire.biggest_concern} />
            <Detail k="Employment" v={questionnaire.employment_status} />
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}

function Detail({ k, v, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <span className="text-[#6F8FA8] text-xs uppercase tracking-wider font-semibold">{k}: </span>
      <span className="text-[#0D2F4D]">{v || "—"}</span>
    </div>
  );
}

export default function Admin() {
  const [pw, setPw] = useState(() => localStorage.getItem(PW_KEY) || "");
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authExchanging, setAuthExchanging] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "relocation-leads";
  const setTab = useCallback((key) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", key);
      // Switching top-level section closes any open detail view.
      p.delete("candidate");
      p.delete("employer");
      return p;
    });
  }, [setSearchParams]);
  const [pendingEmployerId, setPendingEmployerId] = useState(null);
  const openEmployerFromBoard = (id) => {
    if (!id || id === "__live_board__") { setTab("live-board"); return; }
    setPendingEmployerId(id);
    setTab("active-employers");
  };
  const [leads, setLeads] = useState([]);
  const [questionnaires, setQuestionnaires] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [areaFilter, setAreaFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [leadActiveFilter, setLeadActiveFilter] = useState("active");
  const [bulkChangingActive, setBulkChangingActive] = useState(false);
  const [leadEmailOpen, setLeadEmailOpen] = useState(false);
  const [leadEmailSubject, setLeadEmailSubject] = useState("");
  const [leadEmailBody, setLeadEmailBody] = useState("");
  const [leadEmailSender, setLeadEmailSender] = useState("");
  const [leadEmailSending, setLeadEmailSending] = useState(false);
  const [sysFlags, setSysFlags] = useState(null);

  // Poll the system-flag endpoint once auth is in place — used by the yellow
  // "Emails paused" banner so the team knows why welcomes/receipts stop
  // landing in inboxes.
  useEffect(() => {
    if (!pw) return;
    let cancelled = false;
    axios.get(`${API}/admin/system/flags`, { headers: { "X-Admin-Password": pw } })
      .then((r) => { if (!cancelled) setSysFlags(r.data); })
      .catch(() => { /* silent — banner just stays hidden if the fetch fails */ });
    return () => { cancelled = true; };
  }, [pw]);

  // ── 1. On mount: if URL has #session_id=…, exchange it for a persistent session_token
  useEffect(() => {
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    const sessionId = decodeURIComponent(match[1]);
    // Clean the fragment so a refresh doesn't retry an expired session_id
    window.history.replaceState({}, "", window.location.pathname + window.location.search);
    setAuthExchanging(true);
    axios
      .post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then((res) => {
        const token = res.data?.session_token;
        const u = res.data?.user;
        if (!token) throw new Error("No session_token in response");
        localStorage.setItem(PW_KEY, token);
        if (u) localStorage.setItem("ditch_admin_user", JSON.stringify(u));
        setUser(u || null);
        setPw(token);
        toast.success(`Signed in as ${u?.email || "admin"}`);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) {
          setAuthError("denied");
          toast.error("Access denied — company Google account required.");
        } else {
          setAuthError("failed");
          toast.error(err?.response?.data?.detail || "Sign-in failed — please try again.");
        }
      })
      .finally(() => setAuthExchanging(false));
  }, []);

  // ── 2. Whenever we have a token, resolve /auth/me to hydrate user
  useEffect(() => {
    if (!pw) { setUser(null); return; }
    // Try cache first for snappy render
    try {
      const cached = JSON.parse(localStorage.getItem("ditch_admin_user") || "null");
      if (cached && !user) setUser(cached);
    } catch (err) { console.warn("Admin: could not parse cached user", err); }
    axios
      .get(`${API}/auth/me`, { headers: { "X-Admin-Password": pw } })
      .then((res) => {
        const u = res.data?.user || null;
        setUser(u);
        if (u) localStorage.setItem("ditch_admin_user", JSON.stringify(u));
      })
      .catch(() => {
        // token expired or legacy pw — still allow legacy pw flow to work
      });
  }, [pw]);

  const load = useCallback(async (password) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Password": password } });
      setLeads(res.data.leads || []);
      try {
        const qres = await axios.get(`${API}/admin/questionnaires`, { headers: { "X-Admin-Password": password } });
        const map = {};
        (qres.data.questionnaires || []).forEach((q) => {
          map[(q.email || "").toLowerCase()] = q;
        });
        setQuestionnaires(map);
      } catch (qerr) {
        console.warn("Questionnaire enrichment skipped:", qerr?.message || qerr);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(PW_KEY);
        localStorage.removeItem("ditch_admin_user");
        setPw("");
        setUser(null);
      }
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pw) load(pw);
  }, [pw, load]);

  const onStatusChange = async (id, lead_status) => {
    try {
      await axios.patch(
        `${API}/admin/leads/${id}/status`,
        { lead_status },
        { headers: { "X-Admin-Password": pw } }
      );
      setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, lead_status } : l)));
      toast.success(`Status updated → ${lead_status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const onMarkSettled = async (lead) => {    const name = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.email;
    if (!window.confirm(`Mark ${name} as settled in Australia?\n\nThis fires the "Client Settled" post-move email flow.`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/leads/${lead.id}/mark-settled`,
        {},
        { headers: { "X-Admin-Password": pw } }
      );
      setLeads((arr) =>
        arr.map((l) => (l.id === lead.id ? { ...l, settled_at: res.data.settled_at, lead_status: "Settled" } : l))
      );
      toast.success(
        res.data.klaviyo_fired
          ? `${name} marked as settled · Client Settled email fired`
          : `${name} marked as settled (email skipped: ${res.data.reason || "see logs"})`
      );
    } catch (e) {
      toast.error("Failed to mark as settled");
    }
  };

  const onPromoteLead = async (lead, { mode }) => {
    // Sep 2026: one-click promote a relocation lead → careers_candidates
    // as candidate_type=relocating. "Job Support" forces package=none.
    // "With Package" prompts Cara for the package id (auto-detect from
    // payments if she just hits enter).
    const PACKAGE_OPTS = "landing_package | settlement_package | full_concierge_package | family_move_package";
    const name = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.email;
    let body = {};
    if (mode === "job_support") {
      if (!window.confirm(`Move ${name} into Candidates as Job Support only?`)) return;
      body = { force_job_support_only: true };
    } else {
      const pkg = window.prompt(
        `Move ${name} into Candidates. Which package?\n\n${PACKAGE_OPTS}\n\nLeave blank to auto-detect from Stripe/PayPal payments.`,
        ""
      );
      if (pkg === null) return;
      body = pkg.trim() ? { package_purchased: pkg.trim() } : {};
    }
    try {
      const res = await axios.post(
        `${API}/admin/workspace/relocation-leads/${lead.id}/promote-to-candidate`,
        body,
        { headers: { "X-Admin-Password": pw } }
      );
      setLeads((arr) => arr.map((l) => (l.id === lead.id
        ? { ...l, promoted_to_candidate_id: res.data.candidate_id, lead_stage: "converted" }
        : l)));
      const label = res.data.package_label || res.data.package_purchased || "job_support_only";
      toast.success(`${name} moved to Candidates · ${label}`);
    } catch (e) {
      toast.error(`Promote failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onDeleteLead = async (lead) => {
    const name = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.email || "this lead";
    if (!window.confirm(`Delete ${name}?\n\nThis permanently removes the lead. This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, { headers: { "X-Admin-Password": pw } });
      setLeads((arr) => arr.filter((l) => l.id !== lead.id));
      setSelectedLeads((prev) => { const n = new Set(prev); n.delete(lead.id); return n; });
      toast.success(`Deleted ${name}.`);
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const toggleLeadSelect = (id) => setSelectedLeads((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearLeadSel = () => setSelectedLeads(new Set());

  const bulkDeleteLeads = async () => {
    if (selectedLeads.size === 0) return;
    if (!window.confirm(`Delete ${selectedLeads.size} lead${selectedLeads.size === 1 ? "" : "s"}?\n\nThis permanently removes them from the database. This can't be undone.`)) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedLeads);
      const r = await axios.post(
        `${API}/admin/leads/bulk-delete`,
        { ids },
        { headers: { "X-Admin-Password": pw } },
      );
      const { count, requested } = r.data;
      const deletedSet = new Set(ids);
      setLeads((arr) => arr.filter((l) => !deletedSet.has(l.id)));
      clearLeadSel();
      toast.success(`Deleted ${count} of ${requested} leads.`);
    } catch (e) {
      toast.error(`Bulk delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBulkDeleting(false); }
  };

  const bulkSetLeadsActive = async () => {
    if (selectedLeads.size === 0) return;
    const makeActive = leadActiveFilter === "inactive";
    if (!window.confirm(
      `${makeActive ? "Reactivate" : "Mark"} ${selectedLeads.size} relocation lead${selectedLeads.size === 1 ? "" : "s"} ${makeActive ? "as active" : "inactive"}?` +
      (makeActive ? "\n\nTheir previous marketing unsubscribe will stay in place." : "\n\nThis keeps their CRM history, removes them from marketing, and stops active automations."),
    )) return;
    setBulkChangingActive(true);
    try {
      const r = await axios.post(
        `${API}/admin/leads/bulk-active`,
        { ids: Array.from(selectedLeads), active: makeActive },
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(makeActive
        ? `Reactivated ${r.data.updated} relocation lead${r.data.updated === 1 ? "" : "s"}.`
        : `Marked ${r.data.updated} inactive · ${r.data.emails_suppressed || 0} removed from marketing.`);
      clearLeadSel();
      await load(pw);
    } catch (e) {
      toast.error(`Status update failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBulkChangingActive(false); }
  };

  const leadEmailTargets = leads.filter((l) => selectedLeads.has(l.id));
  const leadEmailWithAddr = leadEmailTargets.filter((l) => l.email);
  const sendLeadsBulkEmail = async () => {
    if (!leadEmailSubject.trim() || !leadEmailBody.trim()) { toast.error("Add a subject and a message."); return; }
    if (leadEmailWithAddr.length === 0) { toast.error("None of the selected leads have an email address."); return; }
    setLeadEmailSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/workspace/relocation-leads/bulk-email`,
        { ids: leadEmailWithAddr.map((l) => l.id), subject: leadEmailSubject, body: leadEmailBody, brand: "collective", sender_admin_email: leadEmailSender || null },
        { headers: { "X-Admin-Password": pw } },
      );
      const { sent = 0, failed = 0, skipped_no_email = 0 } = r.data || {};
      const parts = [`Sent ${sent}`];
      if (failed) parts.push(`${failed} failed`);
      if (skipped_no_email) parts.push(`${skipped_no_email} had no email`);
      if (sent > 0) toast.success(parts.join(" · ") + "."); else toast.error(parts.join(" · ") + ".");
      setLeadEmailOpen(false); setLeadEmailSubject(""); setLeadEmailBody("");
      clearLeadSel();
    } catch (e) {
      toast.error(`Email failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLeadEmailSending(false); }
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (leadActiveFilter === "active" && l.active === false) return false;
      if (leadActiveFilter === "inactive" && l.active !== false) return false;
      if (filter === "Hot" && l.lead_priority !== "Hot") return false;
      if (filter === "Warm" && l.lead_priority !== "Warm") return false;
      if (filter === "Needs contact" && l.lead_status !== "New") return false;
      if (areaFilter !== "All" && l.melbourne_area !== areaFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${l.first_name} ${l.last_name} ${l.email} ${l.phone_number}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, filter, areaFilter, search, leadActiveFilter]);

  const stats = useMemo(() => ({
    total: leads.length,
    hot: leads.filter((l) => l.lead_priority === "Hot").length,
    contacted: leads.filter((l) => l.lead_status === "Contacted").length,
    converted: leads.filter((l) => l.lead_status === "Converted").length,
  }), [leads]);

  const areas = useMemo(
    () => ["All", ...Array.from(new Set(leads.map((l) => l.melbourne_area).filter(Boolean)))],
    [leads]
  );

  const exportCsv = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ditch-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authExchanging) {
    return (
      <div className="min-h-screen bg-[#0D2F4D] flex items-center justify-center px-5" data-testid="admin-auth-exchanging">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center">
          <p className="text-sm text-[#6F8FA8]">Signing you in…</p>
        </div>
      </div>
    );
  }

  if (!pw) return <GoogleSignIn onLogin={setPw} initialError={authError} />;

  const displayName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email
    : "";
  const roleLabel = user?.role ? (ROLE_LABEL[user.role] || user.role) : "";

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { "X-Admin-Password": pw },
        withCredentials: true,
      });
    } catch (err) { console.warn("Admin: logout call failed (best-effort)", err); }
    localStorage.removeItem(PW_KEY);
    localStorage.removeItem("ditch_admin_user");
    setPw("");
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-[#F2F2EF] overflow-x-hidden">
      <header className="bg-[#0D2F4D] text-white sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-white rounded-full p-1 flex items-center justify-center">
              <img src="/ditch-badge.png" alt="" className="w-full h-full object-contain" />
            </span>
            <span className="font-display font-extrabold tracking-wide text-sm md:text-base">
              ADMIN · THE DITCH COLLECTIVE
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div data-testid="admin-current-user" className="hidden sm:flex items-center gap-3 text-right">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover border-2 border-[#C6D21F]"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-[#C6D21F] text-[#0D2F4D] font-bold flex items-center justify-center text-xs">
                    {(user.first_name?.[0] || user.email?.[0] || "?").toUpperCase()}
                  </span>
                )}
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-white" data-testid="admin-user-email">
                    {displayName}
                  </p>
                  {roleLabel && (
                    <p className="text-[10px] uppercase tracking-widest text-[#C6D21F]" data-testid="admin-user-role">
                      {roleLabel}
                    </p>
                  )}
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              data-testid="admin-logout"
              onClick={handleLogout}
              className="text-[#A8C0D2] hover:text-[#C6D21F] hover:bg-white/5"
            >
              <LogOut size={16} /> Log out
            </Button>
          </div>
        </div>
      </header>

      {sysFlags && !sysFlags.email_sending_enabled && (
        <div
          className="bg-[#FEF3C7] border-b-2 border-[#F59E0B] text-[#78350F]"
          role="alert"
          data-testid="paused-email-banner"
        >
          <div className="max-w-[1600px] mx-auto px-5 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-widest bg-[#F59E0B] text-white px-2 py-0.5 rounded-full">
              Paused
            </span>
            <p className="text-sm font-semibold">
              All outbound emails are frozen — <span className="font-mono">EMAIL_SENDING_ENABLED=false</span>.
              Leads still save, but no welcomes, receipts or team alerts are going out. Flip the
              env var to <span className="font-mono">true</span> and redeploy to resume.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-5 md:px-8 pt-6 pb-16 flex flex-col md:flex-row gap-6">
        {/* ── Sidebar — grouped by business funnel ─────────────────────── */}
        <aside className="md:w-64 flex-shrink-0" data-testid="admin-sidebar">
          <nav className="bg-white rounded-2xl border border-[#E4E7EA] p-3 md:sticky md:top-24 space-y-4">
            <NavGroup
              label="Careers · Recruitment"
              accent="#0D2F4D"
              items={[
                { key: "candidates",       label: "Candidates",       icon: Briefcase },
                { key: "candidate-enquiries", label: "Candidate Enquiries", icon: Mail },
                { key: "job-intake",        label: "Job Intake",        icon: ClipboardPaste },
                { key: "outreach-targets", label: "Outreach Targets", icon: Building2 },
                { key: "active-employers", label: "Active Employers", icon: CheckCircle2 },
                { key: "live-board",       label: "Live Board",       icon: Radio },
                { key: "resume-reviews",   label: "Resumes",          icon: FileText },
                { key: "assessments",      label: "Assessments",      icon: ClipboardCheck },
                { key: "ad-manager",       label: "Ad Manager",       icon: Megaphone },
              ]}
              activeKey={tab}
              onSelect={setTab}
            />
            <NavGroup
              label="Collective · Relocation"
              accent="#0D2F4D"
              items={[
                { key: "relocation-leads",   label: "Relocation Leads",   icon: PlaneTakeoff },
                { key: "relocation-clients", label: "Relocation Clients", icon: Home },
                { key: "members",            label: "Members",            icon: Users },
              ]}
              activeKey={tab}
              onSelect={setTab}
            />
            <NavGroup
              label="Operations"
              accent="#0D2F4D"
              items={[
                { key: "workspace",    label: "Workspace",   icon: LayoutDashboard },
                { key: "email-hub",    label: "Email Hub",   icon: Send },
                { key: "finance",      label: "Finance",     icon: DollarSign },
                { key: "activity",     label: "Activity",    icon: ActivityIcon },
                { key: "integrations", label: "Integrations", icon: Plug },
              ]}
              activeKey={tab}
              onSelect={setTab}
            />
          </nav>
        </aside>

        {/* ── Main content ─────────────────────────────────────────────── */}
        <main className="flex-1 min-w-0" data-testid="admin-main">

        {/* Careers group */}
        {tab === "candidates" && searchParams.get("view") === "shortlist" && (
          <ShortlistAdmin pw={pw} />
        )}
        {tab === "candidates" && searchParams.get("view") !== "shortlist" && (
          <WorkspaceTab pw={pw} user={user} lockedSubTab="candidates" hideBusinessSwitcher />
        )}
        {tab === "candidate-enquiries" && <CandidateEnquiries pw={pw} />}
        {tab === "job-intake" && <AdminJobIntake pw={pw} />}
        {tab === "outreach-targets" && (
          <WorkspaceTab pw={pw} user={user} lockedSubTab="employers" hideBusinessSwitcher employerStatusFilter="prospect" />
        )}
        {tab === "active-employers" && (
          <WorkspaceTab pw={pw} user={user} lockedSubTab="employers" hideBusinessSwitcher employerStatusFilter="active_client" initialEmployerId={pendingEmployerId} />
        )}
        {tab === "live-board" && (
          <LiveBoardTab pw={pw} onOpenEmployer={openEmployerFromBoard} />
        )}
        {/* Legacy alias so any bookmarked ?tab=employers link keeps working. */}
        {tab === "employers" && (
          <WorkspaceTab pw={pw} user={user} lockedSubTab="employers" hideBusinessSwitcher employerStatusFilter="prospect" />
        )}
        {tab === "resume-reviews" && <ResumeReviewsTab pw={pw} />}
        {tab === "assessments" && <AssessmentsTab pw={pw} />}
        {tab === "ad-manager" && <AdManagerTab pw={pw} />}

        {/* Collective group */}
        {tab === "relocation-clients" && <ClientsTab pw={pw} />}
        {tab === "members" && <MembersTab pw={pw} />}

        {/* Operations group */}
        {tab === "workspace" && (
          <WorkspaceTab pw={pw} user={user} lockedSubTab="my-day" hideBusinessSwitcher />
        )}
        {tab === "email-hub" && <EmailHubTab pw={pw} />}
        {tab === "finance" && <FinancePage pw={pw} />}
        {tab === "activity" && <ActivityTab pw={pw} />}
        {tab === "integrations" && <IntegrationsTab user={user} />}

        {/* Relocation Leads (formerly the Leads tab) */}
        {tab === "relocation-leads" && (
        <>
        {/* Live sales feed — last 20 purchases across Stripe + PayPal */}
        <SalesFeed />

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { k: "total", label: "Total", v: stats.total },
            { k: "hot", label: "Hot", v: stats.hot },
            { k: "contacted", label: "Contacted", v: stats.contacted },
            { k: "converted", label: "Converted", v: stats.converted },
          ].map((s) => (
            <Card key={s.k} data-testid={ADMIN.statCard(s.k)} className="border-[#E4E7EA]">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">{s.label}</p>
                <p className="font-display text-4xl font-extrabold text-[#0D2F4D] mt-2">{s.v}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-[#E4E7EA] mb-6">
          <CardContent className="p-5 flex flex-wrap gap-3 items-center">
            {["All", "Hot", "Warm", "Needs contact"].map((f) => (
              <Button
                key={f}
                data-testid={ADMIN.filter(f.toLowerCase().replace(/\s/g, "-"))}
                onClick={() => setFilter(f)}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                className={filter === f
                  ? "bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 rounded-full"
                  : "border-[#D6DCE3] text-[#0D2F4D] rounded-full"}
              >
                {f}
              </Button>
            ))}
            <Select value={areaFilter} onValueChange={setAreaFilter}>
              <SelectTrigger
                data-testid={ADMIN.filter("area")}
                className="w-[160px] rounded-full border-[#D6DCE3] text-[#0D2F4D] font-semibold text-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {areas.map((a) => (
                  <SelectItem key={a} value={a}>{a === "All" ? "All areas" : a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={leadActiveFilter} onValueChange={(value) => { setLeadActiveFilter(value); clearLeadSel(); }}>
              <SelectTrigger
                data-testid="leads-active-filter"
                className="w-[160px] rounded-full border-[#D6DCE3] text-[#0D2F4D] font-semibold text-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active records</SelectItem>
                <SelectItem value="inactive">Inactive records</SelectItem>
                <SelectItem value="all">All records</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative ml-auto flex-1 min-w-[200px] max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6F8FA8] z-10" />
              <Input
                data-testid="leads-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone…"
                className="pl-9 border-[#D6DCE3] focus-visible:ring-[#0D2F4D]"
              />
            </div>
            <Button
              data-testid={ADMIN.exportCsv}
              onClick={exportCsv}
              size="sm"
              className="bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90"
            >
              <Download size={14} /> Export CSV
            </Button>
          </CardContent>
        </Card>

        {/* Leads */}
        {loading ? (
          <p className="text-center text-[#6F8FA8] py-20">Loading leads…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]">
            <p className="text-[#6F8FA8]">No leads match this view yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Bulk selection bar */}
            <div className="bg-white rounded-2xl border border-[#E4E7EA] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="leads-bulk-bar">
              <input
                type="checkbox"
                data-testid="leads-select-all"
                checked={filtered.length > 0 && filtered.every((l) => selectedLeads.has(l.id))}
                onChange={(e) => {
                  if (e.target.checked) setSelectedLeads(new Set(filtered.map((l) => l.id)));
                  else clearLeadSel();
                }}
                className="w-4 h-4 rounded cursor-pointer"
                title="Select all filtered leads"
              />
              <span className="text-xs font-semibold text-[#6F8FA8]" data-testid="leads-selected-count">
                {selectedLeads.size > 0
                  ? `${selectedLeads.size} selected`
                  : `Select all (${filtered.length})`}
              </span>
              {selectedLeads.size > 0 && (
                <>
                  <button
                    onClick={bulkSetLeadsActive}
                    disabled={bulkChangingActive}
                    data-testid="leads-bulk-active-toggle"
                    className="ml-auto text-xs font-bold px-3 py-1.5 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#B7C31C] disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    {leadActiveFilter === "inactive" ? <RotateCcw size={12}/> : <UserX size={12}/>}
                    {bulkChangingActive ? "Updating…" : leadActiveFilter === "inactive" ? `Reactivate ${selectedLeads.size}` : `Mark ${selectedLeads.size} inactive`}
                  </button>
                  {leadActiveFilter !== "inactive" && (
                  <button
                    onClick={() => { setLeadEmailSubject(""); setLeadEmailBody(""); setLeadEmailOpen(true); }}
                    data-testid="leads-bulk-email-btn"
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] inline-flex items-center gap-1.5"
                  >
                    <Mail size={12} /> Email {selectedLeads.size}
                  </button>)}
                  <button
                    onClick={bulkDeleteLeads}
                    disabled={bulkDeleting}
                    data-testid="leads-bulk-delete-btn"
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <Trash2 size={12} />
                    {bulkDeleting ? "Deleting…" : `Delete selected (${selectedLeads.size})`}
                  </button>
                  <button
                    onClick={clearLeadSel}
                    data-testid="leads-bulk-clear"
                    className="text-xs text-[#6F8FA8] hover:text-[#0D2F4D] underline"
                  >
                    Clear
                  </button>
                </>
              )}
            </div>
            <Dialog open={leadEmailOpen} onOpenChange={(o) => !o && setLeadEmailOpen(false)}>
              <DialogContent className="max-w-lg" data-testid="leads-bulk-email-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><Mail size={16}/> Email selected leads</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[#6F8FA8]">
                  One-off email to <strong>{leadEmailWithAddr.length}</strong> lead{leadEmailWithAddr.length === 1 ? "" : "s"}
                  {leadEmailTargets.length !== leadEmailWithAddr.length && (
                    <> · {leadEmailTargets.length - leadEmailWithAddr.length} skipped (no email)</>
                  )}. Use <code className="text-[11px] bg-[#F2F2EF] px-1 rounded">{"{{first_name}}"}</code> to personalise.
                </p>
                <EmailComposerExtras
                  pw={pw}
                  brand="collective"
                  senderEmail={leadEmailSender}
                  onSender={setLeadEmailSender}
                  onLoadTemplate={(t) => { setLeadEmailSubject(t.subject || ""); setLeadEmailBody(t.body || ""); }}
                />
                <input
                  type="text"
                  value={leadEmailSubject}
                  onChange={(e) => setLeadEmailSubject(e.target.value)}
                  placeholder="Subject"
                  data-testid="leads-bulk-email-subject"
                  className="w-full text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D]"
                />
                <textarea
                  value={leadEmailBody}
                  onChange={(e) => setLeadEmailBody(e.target.value)}
                  placeholder={"Hi {{first_name}},\n\nWrite your message here…"}
                  rows={8}
                  data-testid="leads-bulk-email-body"
                  className="w-full text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D] resize-y"
                />
                <DialogFooter>
                  <button onClick={() => setLeadEmailOpen(false)} className="text-sm font-medium px-4 py-2 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]">Cancel</button>
                  <button
                    onClick={sendLeadsBulkEmail}
                    disabled={leadEmailSending || leadEmailWithAddr.length === 0}
                    data-testid="leads-bulk-email-send"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50"
                  >
                    <Mail size={13}/>{leadEmailSending ? "Sending…" : `Send to ${leadEmailWithAddr.length}`}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {filtered.map((l) => (
              <LeadCard
                key={l.id}
                lead={l}
                questionnaire={questionnaires[(l.email || "").toLowerCase()]}
                onStatusChange={onStatusChange}
                onMarkSettled={onMarkSettled}
                onPromote={onPromoteLead}
                onDelete={onDeleteLead}
                selected={selectedLeads.has(l.id)}
                onToggleSelect={toggleLeadSelect}
              />
            ))}
          </div>
        )}
        </>
        )}
        </main>
      </div>
      <PulseTicker pw={pw} />
    </div>
  );
}

/* ── Sidebar nav group — labeled section + clickable rows ─────────── */
function NavGroup({ label, items, activeKey, onSelect, accent = "#0D2F4D" }) {
  return (
    <div data-testid={`nav-group-${label.split(" ")[0].toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] px-3 pt-1 pb-2">
        {label}
      </p>
      <ul className="space-y-0.5">
        {items.map(({ key, label: item, icon: Icon }) => {
          const active = activeKey === key;
          return (
            <li key={key}>
              <a
                href={`/admin?tab=${key}`}
                data-testid={ADMIN.tab(key)}
                onClick={(e) => {
                  // Let the browser handle new-tab / new-window intents
                  // (cmd/ctrl/shift/middle-click) natively; otherwise do a
                  // fast in-app switch without a full page reload.
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                  e.preventDefault();
                  onSelect(key);
                }}
                className={
                  "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors text-left no-underline " +
                  (active
                    ? "bg-[#0D2F4D] text-[#C6D21F]"
                    : "text-[#0D2F4D] hover:bg-[#F2F2EF]")
                }
                style={active ? { boxShadow: `inset 3px 0 0 ${accent}` } : {}}
              >
                <Icon size={15} className={active ? "text-[#C6D21F]" : "text-[#6F8FA8]"} />
                <span>{item}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
